<div class="domain-import-results">
    <!-- Results Header -->
    <div class="results-header mb-4">
        <div class="row align-items-center">
            <div class="col-md-32">
                <h5 translate>Import Results</h5>
                <p class="text-muted mb-0" [class]="getStatusColorClass()">
                    <i [class]="getStatusIcon()" class="me-2"></i>
                    {{ getStatusMessage() }}
                </p>
            </div>
            <div class="col-md-16 text-end">
                <div class="results-stats">
                    <div class="stat-item">
                        <div class="stat-numbers">
                            <span class="stat-number" [class]="getStatusColorClass()">{{ importSummary?.successCount || 0 }}</span>
                            <span class="stat-divider">/</span>
                            <span class="stat-total">{{ importSummary?.totalDomains || 0 }}</span>
                        </div>
                        <div class="stat-label" translate>Success Rate</div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Summary Statistics -->
    <div class="summary-statistics mb-4">
        <div class="row">
            <div class="col-md-12 col-24">
                <div class="summary-card success">
                    <div class="summary-icon">
                        <i class="fas fa-check-circle"></i>
                    </div>
                    <div class="summary-content">
                        <div class="summary-number">{{ importSummary?.successCount || 0 }}</div>
                        <div class="summary-label" translate>Successful</div>
                    </div>
                </div>
            </div>
            
            <div class="col-md-12 col-24">
                <div class="summary-card failed">
                    <div class="summary-icon">
                        <i class="fas fa-times-circle"></i>
                    </div>
                    <div class="summary-content">
                        <div class="summary-number">{{ importSummary?.failureCount || 0 }}</div>
                        <div class="summary-label" translate>Failed</div>
                    </div>
                </div>
            </div>
            
            <div class="col-md-12 col-24">
                <div class="summary-card duration">
                    <div class="summary-icon">
                        <i class="fas fa-clock"></i>
                    </div>
                    <div class="summary-content">
                        <div class="summary-number">{{ getImportDuration() }}</div>
                        <div class="summary-label" translate>Duration</div>
                    </div>
                </div>
            </div>
            
            <div class="col-md-12 col-24">
                <div class="summary-card rate">
                    <div class="summary-icon">
                        <i class="fas fa-tachometer-alt"></i>
                    </div>
                    <div class="summary-content">
                        <div class="summary-number">{{ getImportRate() }}</div>
                        <div class="summary-label" translate>Rate</div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Results Tabs -->
    <div class="results-tabs mb-4" *ngIf="importSummary?.results?.length">
        <ul class="nav nav-tabs">
            <li class="nav-item">
                <button
                    class="nav-link"
                    [class.active]="currentTab === 'success'"
                    (click)="switchTab('success')"
                    type="button">
                    <i class="fas fa-check-circle text-success me-2"></i>
                    <span translate>Successful</span>
                    <span class="badge bg-success ms-2">{{ getSuccessfulResults().length }}</span>
                </button>
            </li>
            <li class="nav-item" *ngIf="getFailedResults().length > 0">
                <button
                    class="nav-link"
                    [class.active]="currentTab === 'failed'"
                    (click)="switchTab('failed')"
                    type="button">
                    <i class="fas fa-times-circle text-danger me-2"></i>
                    <span translate>Failed</span>
                    <span class="badge bg-danger ms-2">{{ getFailedResults().length }}</span>
                </button>
            </li>
        </ul>
    </div>

    <!-- Results Content -->
    <div class="results-content" *ngIf="importSummary?.results?.length">
        <!-- Successful Results -->
        <div class="tab-content" *ngIf="currentTab === 'success'">
            <div class="alert alert-success" *ngIf="getSuccessfulResults().length === 0">
                <i class="fas fa-info-circle me-2"></i>
                <span translate>No domains were successfully imported.</span>
            </div>
            
            <div class="results-table" *ngIf="getSuccessfulResults().length > 0">
                <div class="table-responsive">
                    <table class="table table-striped">
                        <thead>
                            <tr>
                                <th translate>Domain</th>
                                <th translate>URL</th>
                                <th translate>Created</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr *ngFor="let result of getPaginatedResults(); let i = index">
                                <td>
                                    <div class="domain-info">
                                        <i class="fas fa-check-circle text-success me-2"></i>
                                        <strong>{{ result.domain.title }}</strong>
                                        <div class="domain-index text-muted small">
                                            #{{ ((currentPage - 1) * pageSize) + i + 1 }}
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    <code class="domain-url">{{ result.domain.url }}</code>
                                </td>
                                <td>
                                    <span class="badge bg-success" translate>Success</span>
                                    <div class="text-muted small" *ngIf="result.createdDomain?.id">
                                        ID: {{ result.createdDomain.id }}
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <!-- Failed Results -->
        <div class="tab-content" *ngIf="currentTab === 'failed'">
            <div class="alert alert-info" *ngIf="getFailedResults().length === 0">
                <i class="fas fa-check-circle me-2"></i>
                <span translate>All domains were imported successfully - no failures to display.</span>
            </div>
            
            <div class="results-table" *ngIf="getFailedResults().length > 0">
                <div class="alert alert-warning mb-3">
                    <i class="fas fa-exclamation-triangle me-2"></i>
                    <span translate>The following domains could not be imported. You can download a detailed error report for further analysis.</span>
                </div>
                
                <div class="table-responsive">
                    <table class="table table-striped">
                        <thead>
                            <tr>
                                <th translate>Domain</th>
                                <th translate>URL</th>
                                <th translate>Error</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr *ngFor="let result of getPaginatedResults(); let i = index">
                                <td>
                                    <div class="domain-info">
                                        <i class="fas fa-times-circle text-danger me-2"></i>
                                        <strong>{{ result.domain.title }}</strong>
                                        <div class="domain-index text-muted small">
                                            #{{ ((currentPage - 1) * pageSize) + i + 1 }}
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    <code class="domain-url">{{ result.domain.url }}</code>
                                </td>
                                <td>
                                    <div class="error-info">
                                        <span class="badge bg-danger mb-1" translate>Failed</span>
                                        <div class="error-message small text-muted" [title]="result.error">
                                            {{ getErrorPreview(result.error || 'Unknown error') }}
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <!-- Pagination -->
        <nav aria-label="Results pagination" *ngIf="getTotalPages() > 1" class="mt-3">
            <ul class="pagination justify-content-center">
                <li class="page-item" [class.disabled]="currentPage === 1">
                    <button class="page-link" (click)="goToPage(currentPage - 1)" [disabled]="currentPage === 1">
                        <i class="fas fa-chevron-left"></i>
                        <span translate>Previous</span>
                    </button>
                </li>
                
                <li class="page-item"
                    *ngFor="let page of getPageNumbers()"
                    [class.active]="page === currentPage">
                    <button class="page-link" (click)="goToPage(page)">{{ page }}</button>
                </li>
                
                <li class="page-item" [class.disabled]="currentPage === getTotalPages()">
                    <button class="page-link" (click)="goToPage(currentPage + 1)" [disabled]="currentPage === getTotalPages()">
                        <span translate>Next</span>
                        <i class="fas fa-chevron-right"></i>
                    </button>
                </li>
            </ul>
        </nav>
    </div>

    <!-- Error Report Download -->
    <div class="error-report-section mt-4" *ngIf="getFailedResults().length > 0">
        <div class="card">
            <div class="card-body text-center">
                <h6 translate>Download Error Report</h6>
                <p class="text-muted mb-3" translate>
                    Get a detailed report of all failed imports for troubleshooting and analysis.
                </p>
                <button
                    type="button"
                    class="btn btn-outline-danger"
                    (click)="onDownloadErrorReport()"
                    data-test="download-error-report">
                    <i class="fas fa-download me-2"></i>
                    <span translate>Download Error Report</span>
                </button>
            </div>
        </div>
    </div>

    <!-- Empty State -->
    <div class="empty-state text-center py-5" *ngIf="!importSummary?.results?.length">
        <i class="fas fa-inbox text-muted" style="font-size: 3rem;"></i>
        <h5 class="text-muted mt-3" translate>No Import Results</h5>
        <p class="text-muted" translate>Import data is not available.</p>
    </div>
</div>

